import React, { Component } from "react";
import { connect } from "react-redux";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { BlueBtn } from "components/Controls";
import { CustomCheckbox } from "components/Controls";
import {
  loadingSelector,
  activeStepDataSelector,
} from "containers/MultistepForm/selectors";
import { nextStep } from "containers/MultistepForm/actions";
import { addThousandsSeparator } from "utils/helpers";
import { required } from "utils/validations";
import URLS from "utils/import_urls";
import KafeneLogo from "assets/img/kafene-logo.png";
import { tokenSelector } from "../../../../EmbeddableWidget/selectors";
import { ProvidersApi } from "../../../../../api/providers";

import "./styles.scss";

const selector = formValueSelector('kafene_offer_details_form');

class KafeneOfferDetails extends Component {
  state = {
    valid: false,
    loadingContract: false,
    error: null,
  };

  componentDidMount() {
    this.checkInitialValidity();
  }

  checkInitialValidity = () => {
    const { formValues } = this.props;
    const valid = !!formValues.isElectronicSignatureChecked && !!formValues.isMessagingChecked;
    this.setState({ valid });
  };

  handleChange = () => {
    const { formValues } = this.props;
    const valid = !!formValues.isElectronicSignatureChecked && !!formValues.isMessagingChecked;
    this.setState({ valid });
  };

  handleContinue = (e) => {
    e.preventDefault();
    const { offer, nextStep, valid, token } = this.props;
    const { valid: stateValid } = this.state;
    
    if (valid && stateValid) {
      this.setState({ loadingContract: true, error: null });

      ProvidersApi.kafeneGetContract(token, { app_id: offer.app_id })
        .then((contractData) => {
          if (!contractData.contract_pdf) {
            throw new Error('Contract PDF not available');
          }
          nextStep({
            step: "kafeneContractView",
            data: { ...offer, contract_pdf: contractData.contract_pdf },
          });
        })
        .catch((error) => {
          console.error("Failed to get contract:", error);
          this.setState({
            error: "Unable to load the contract at this time. Please try again or contact support.",
            loadingContract: false
          });
        });
    }
  };

  renderCheckbox = ({ input, meta }) => {
    return (
      <CustomCheckbox className="checkbox" meta={meta} {...input} customChange={this.handleChange} />
    );
  };

  formatCurrency = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "$0.00";
    return `$${addThousandsSeparator(n.toFixed(2))}`;
  };

  calculateEpo1DueDate = () => {
    const epo1Period = this.props.offer?.contract_terms?.epo1_period;
    if (!epo1Period) return "";
    const days = epo1Period;
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + days);
    return dueDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  render() {
    const { loading, offer, valid } = this.props;
    const { valid: stateValid, loadingContract, error } = this.state;
    const offerData = offer || {};

    const isButtonDisabled = loading || loadingContract || !valid || !stateValid;

    return (
      <div className="kafene-offer-details">
        <img src={KafeneLogo} alt="Kafene" className="kafene-offer-details__logo" />
        
        <h1 className="kafene-offer-details__title">Review Your Lease</h1>
        <p className="kafene-offer-details__subtitle">
          Please carefully review and sign the Kafene Lease Agreement
        </p>

        <div className="kafene-offer-details__section">
          <h2 className="kafene-offer-details__section-title">Total Due at Signing</h2>
          <div className="kafene-offer-details__card">
            <div className="kafene-offer-details__total">
              {this.formatCurrency(offerData.contract_terms?.initial_payment)}
            </div>
            <div className="kafene-offer-details__row">
              <span>Initial Payment*</span>
              <span>{this.formatCurrency(offerData.contract_terms?.initial_payment)}</span>
            </div>
            <p className="kafene-offer-details__note">
              *The Initial Payment is applied to your lease balance
            </p>
          </div>
        </div>

        <div className="kafene-offer-details__section">
          <h2 className="kafene-offer-details__section-title">Summary of Lease Terms</h2>
          <div className="kafene-offer-details__card">
            <div className="kafene-offer-details__term">
              <span className="kafene-offer-details__term-label">Your Renewal Payment (with tax)</span>
              <span className="kafene-offer-details__term-value">{this.formatCurrency(offerData.contract_terms?.renewal_payment_with_tax)}</span>
            </div>

            <div className="kafene-offer-details__term">
              <span className="kafene-offer-details__term-label">Term to Ownership</span>
              <span className="kafene-offer-details__term-value">
                {offerData.contract_terms?.ownership_terms || 0} payments
              </span>
            </div>

            <div className="kafene-offer-details__term">
              <span className="kafene-offer-details__term-label">Total of Payments (full term)</span>
              <span className="kafene-offer-details__term-value">{this.formatCurrency(offerData.contract_terms?.total_of_payments_with_tax)}</span>
            </div>

            <div className="kafene-offer-details__term">
              <span className="kafene-offer-details__term-label">Cost of Rental (with tax)</span>
              <span className="kafene-offer-details__term-value">{this.formatCurrency(offerData.contract_terms?.cost_of_rental_with_tax)}</span>
            </div>
          </div>
        </div>

        <div className="kafene-offer-details__section">
          <h2 className="kafene-offer-details__section-title">90-Day Option</h2>
          <p className="kafene-offer-details__description">
            You may purchase the leased merchandise at any time. For the lowest price, pay the price below by the specified date.
          </p>
          <div className="kafene-offer-details__card">
            <div className="kafene-offer-details__term">
              <span className="kafene-offer-details__term-label">90-Day Price</span>
              <span className="kafene-offer-details__term-value">{this.formatCurrency(offerData.contract_terms?.epo1_price_with_tax)}</span>
            </div>

            <div className="kafene-offer-details__term">
              <span className="kafene-offer-details__term-label">90-Day Period</span>
              <span className="kafene-offer-details__term-value">{offerData.contract_terms?.epo1_period || 90} days</span>
            </div>

            <div className="kafene-offer-details__term">
              <span className="kafene-offer-details__term-label">90-Day Due Date</span>
              <span className="kafene-offer-details__term-value">{this.calculateEpo1DueDate()}</span>
            </div>
          </div>
        </div>

        <div className="kafene-offer-details__section">
          <h2 className="kafene-offer-details__section-title">Consents and finalizing</h2>
          <p className="kafene-offer-details__description">
            You may purchase the leased merchandise at any time. For the lowest price, pay the price below by the specified date.
          </p>

          <div className="kafene-offer-details__checkbox-group">
            <div className="flex flex__align--baseline kafene-offer-details__checkbox-item">
              <Field
                name="isElectronicSignatureChecked"
                validate={[required]}
                component={this.renderCheckbox}
              />
              <div>
                I agree to the&nbsp;
                <a href={URLS.DISCLOSURE_AND_CONSENT_KAFENE} className="link" target="_blank" rel="noopener noreferrer">
                  Electronic Record and Signature Disclosure
                </a>
                &nbsp;and to the&nbsp;
                <a href={URLS.MESSAGING_POLICY_KAFENE} className="link" target="_blank" rel="noopener noreferrer">
                  Messaging Policy
                </a>
              </div>
            </div>

            <div className="flex flex__align--baseline kafene-offer-details__checkbox-item">
              <Field
                name="isMessagingChecked"
                validate={[required]}
                component={this.renderCheckbox}
              />
              <div>
                I agree to receive account updates at the phone number provided. Message frequency varies.
                Text STOP to end. Msg & Rates may apply.&nbsp;
                <a href={URLS.MESSAGING_POLICY_KAFENE} className="link" target="_blank" rel="noopener noreferrer">
                  Messaging Policy
                </a>.
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="kafene-offer-details__error">
            {error}
          </div>
        )}

        <div className="kafene-offer-details__footer">
          <BlueBtn
            loading={loadingContract}
            disabled={isButtonDisabled}
            onClick={this.handleContinue}
          >
            View Agreement
          </BlueBtn>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  loading: loadingSelector()(state),
  offer: activeStepDataSelector()(state),
  formValues: selector(state, 'isElectronicSignatureChecked', 'isMessagingChecked'),
  token: tokenSelector()(state),
});

const mapDispatchToProps = (dispatch) => ({
  nextStep: (value) => dispatch(nextStep(value)),
});

KafeneOfferDetails = reduxForm({
  form: 'kafene_offer_details_form',
  initialValues: { isElectronicSignatureChecked: false, isMessagingChecked: false },
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(KafeneOfferDetails);

KafeneOfferDetails = connect(mapStateToProps, mapDispatchToProps)(KafeneOfferDetails);

export default KafeneOfferDetails;
